import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { formatWeekRange, formatWeekRangeForFile } from './weekHelpers';
import {
  computeBalances,
  computeRevenueSummary,
  computeCapitalSummary,
  computeOpexSummary,
  computeVehicleJobDetails,
  computeCashPosition,
  computeBudgetReview,
  computeExpenseBreakdown,
  computeReceivablesAging,
  computePayablesAging,
  computeMonthlySummary,
} from './reportAggregations';
import { EXPENSE_CATEGORIES, BUDGET_SEGMENTS } from '../constants';

const NUM_FMT = '#,##0.00';
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Formula-injection prefixes that Excel may interpret as formulas
const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r', '\n'];

/**
 * Sanitize a string cell value to prevent Excel formula injection.
 * Prefixes dangerous characters with a single-quote so Excel treats them as text.
 */
function safe(v) {
  if (typeof v !== 'string') return v;
  if (v.length > 0 && FORMULA_PREFIXES.some(p => v.startsWith(p))) {
    return "'" + v;
  }
  return v;
}

function curr(v) {
  return typeof v === 'number' ? v : 0;
}

/**
 * Export the full weekly management report as a 4-sheet .xlsx workbook.
 */
export function exportWeeklyReport(data, weekStart, weekEnd) {
  const wb = XLSX.utils.book_new();

  buildReportSheet(wb, data, weekStart, weekEnd);
  buildBudgetReviewSheet(wb, data);
  buildBreakdownSheet(wb, data);
  buildSummarySheet(wb, data);

  const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbOut], { type: XLSX_MIME });
  const fileName = `Weekly_Management_Report_${formatWeekRangeForFile(weekStart, weekEnd)}.xlsx`;
  saveAs(blob, fileName);
}

// ---------------------------------------------------------------------------
// Sheet 1 – Report
// ---------------------------------------------------------------------------
function buildReportSheet(wb, data, weekStart, weekEnd) {
  const { preWeekTransactions, weekTransactions, weekInvoices, jobsByNo, customersByName } = data;
  const rows = [];

  // Title
  rows.push([`WEEKLY MANAGEMENT REPORT — ${formatWeekRange(weekStart, weekEnd)}`]);
  rows.push([]);

  // Balances
  const bal = computeBalances(preWeekTransactions, weekTransactions);
  rows.push(['ACCOUNT BALANCES']);
  rows.push(['Account', 'Opening', 'Week Movement', 'Closing']);
  ['Revenue', 'Opex', 'CAPEX', 'Other'].forEach(a => {
    rows.push([a, curr(bal.opening[a]), curr(bal.delta[a]), curr(bal.closing[a])]);
  });
  rows.push([]);

  // Revenue
  const rev = computeRevenueSummary(weekTransactions);
  rows.push(['REVENUE SUMMARY']);
  rows.push(['Description', 'Invoice No', 'Mode of Payment', 'Amount']);
  rev.forEach(r => rows.push([safe(r.description), safe(r.invoiceNo), safe(r.modeOfPayment), curr(r.amount)]));
  rows.push(['', '', 'Total Revenue', rev.reduce((s, r) => s + r.amount, 0)]);
  rows.push([]);

  // Capital
  const cap = computeCapitalSummary(weekTransactions);
  if (cap.length > 0) {
    rows.push(['CAPITAL SUMMARY']);
    rows.push(['Description', 'Mode of Payment', 'Amount']);
    cap.forEach(r => rows.push([safe(r.description), safe(r.modeOfPayment), curr(r.amount)]));
    rows.push(['', 'Total Capital', cap.reduce((s, r) => s + r.amount, 0)]);
    rows.push([]);
  }

  // Opex
  const opex = computeOpexSummary(weekTransactions);
  rows.push(['OPERATING EXPENDITURE']);
  rows.push(['Description', 'Category', 'Mode of Payment', 'Amount']);
  opex.forEach(r => rows.push([safe(r.description), safe(r.category), safe(r.modeOfPayment), curr(r.amount)]));
  rows.push(['', '', 'Total Opex', opex.reduce((s, r) => s + r.amount, 0)]);
  rows.push([]);

  // Vehicle Job Details
  const jobs = computeVehicleJobDetails(weekInvoices, jobsByNo, customersByName);
  const fleet = jobs.filter(j => j.customerType === 'Fleet/Credit');
  const cash = jobs.filter(j => j.customerType === 'Cash');

  rows.push(['VEHICLE JOB DETAILS — FLEET / CREDIT']);
  rows.push(['Invoice', 'Customer', 'Reg No', 'Job Type', 'Parts', 'Labour', 'VAT', 'Total']);
  fleet.forEach(j => rows.push([safe(j.invoiceNumber), safe(j.customerName), safe(j.registrationNumber), safe(j.jobType), curr(j.spareParts), curr(j.labour), curr(j.vat), curr(j.total)]));
  if (fleet.length > 0) {
    rows.push(['', '', '', 'Fleet Total', fleet.reduce((s, j) => s + j.spareParts, 0), fleet.reduce((s, j) => s + j.labour, 0), fleet.reduce((s, j) => s + j.vat, 0), fleet.reduce((s, j) => s + j.total, 0)]);
  }
  rows.push([]);

  rows.push(['VEHICLE JOB DETAILS — CASH']);
  rows.push(['Invoice', 'Customer', 'Reg No', 'Job Type', 'Parts', 'Labour', 'VAT', 'Total']);
  cash.forEach(j => rows.push([safe(j.invoiceNumber), safe(j.customerName), safe(j.registrationNumber), safe(j.jobType), curr(j.spareParts), curr(j.labour), curr(j.vat), curr(j.total)]));
  if (cash.length > 0) {
    rows.push(['', '', '', 'Cash Total', cash.reduce((s, j) => s + j.spareParts, 0), cash.reduce((s, j) => s + j.labour, 0), cash.reduce((s, j) => s + j.vat, 0), cash.reduce((s, j) => s + j.total, 0)]);
  }
  rows.push([]);

  // Grand total
  rows.push(['GRAND TOTAL — JOB REVENUE']);
  rows.push(['Parts', 'Labour', 'VAT', 'Total']);
  rows.push([
    jobs.reduce((s, j) => s + j.spareParts, 0),
    jobs.reduce((s, j) => s + j.labour, 0),
    jobs.reduce((s, j) => s + j.vat, 0),
    jobs.reduce((s, j) => s + j.total, 0),
  ]);
  rows.push([]);

  // Cash position
  const cp = computeCashPosition(weekTransactions);
  rows.push(['WEEKLY CASH POSITION']);
  rows.push(['Metric', 'Amount']);
  rows.push(['Total Inflows', curr(cp.totalInflows)]);
  rows.push(['Total Outflows', curr(cp.totalOutflows)]);
  rows.push(['Net Cash', curr(cp.net)]);
  rows.push([]);
  rows.push(['Outflow Category', 'Amount']);
  Object.entries(cp.outflowsByCategory).sort((a, b) => b[1] - a[1]).forEach(([cat, amt]) => {
    rows.push([cat, curr(amt)]);
  });

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [
    { wch: 30 }, { wch: 22 }, { wch: 22 }, { wch: 18 },
    { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 18 },
  ];
  applyNumberFormat(ws, rows);
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
}

// ---------------------------------------------------------------------------
// Sheet 2 – Budget Review
// ---------------------------------------------------------------------------
function buildBudgetReviewSheet(wb, data) {
  const { monthInvoices, monthStart, jobsByNo, customersByName } = data;
  const { weeks, segmentData } = computeBudgetReview(monthInvoices, monthStart, jobsByNo, customersByName);
  const rows = [];

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  rows.push([`BUDGET REVIEW — ${monthNames[monthStart.getMonth()]} ${monthStart.getFullYear()}`]);
  rows.push([]);

  // Header
  const header = ['Segment', 'Type', ...weeks.map(w => w.label), 'Total'];
  rows.push(header);

  BUDGET_SEGMENTS.forEach(seg => {
    const sp = segmentData[seg]?.spareParts || [];
    const lb = segmentData[seg]?.labour || [];
    rows.push([seg, 'Parts', ...sp, sp.reduce((s, v) => s + v, 0)]);
    rows.push(['', 'Labour', ...lb, lb.reduce((s, v) => s + v, 0)]);
  });

  // Week totals
  const weekTotals = weeks.map((_, wi) =>
    BUDGET_SEGMENTS.reduce((sum, seg) => sum + (segmentData[seg]?.spareParts[wi] || 0) + (segmentData[seg]?.labour[wi] || 0), 0)
  );
  rows.push(['Week Totals', '', ...weekTotals, weekTotals.reduce((s, v) => s + v, 0)]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 16 }, { wch: 10 }, ...weeks.map(() => ({ wch: 16 })), { wch: 18 }];
  applyNumberFormat(ws, rows);
  XLSX.utils.book_append_sheet(wb, ws, 'Budget Review');
}

// ---------------------------------------------------------------------------
// Sheet 3 – Breakdown
// ---------------------------------------------------------------------------
function buildBreakdownSheet(wb, data) {
  const { weekTransactions, allInvoices, openPayables } = data;
  const rows = [];

  rows.push(['DETAILED EXPENSE BREAKDOWN']);
  rows.push([]);

  const breakdown = computeExpenseBreakdown(weekTransactions);
  rows.push(['Category', 'Description', 'Date', 'Payment Method', 'Amount']);

  let grandTotal = 0;
  EXPENSE_CATEGORIES.forEach(cat => {
    const items = breakdown[cat] || [];
    if (items.length === 0) return;
    const catTotal = items.reduce((s, item) => s + item.amount, 0);
    grandTotal += catTotal;
    items.forEach(item => {
      rows.push([safe(cat), safe(item.description), safe(item.date), safe(item.modeOfPayment), curr(item.amount)]);
    });
    rows.push(['', '', '', `${cat} Subtotal`, curr(catTotal)]);
  });
  rows.push(['', '', '', 'Total Expenses', curr(grandTotal)]);
  rows.push([]);

  // Receivables aging
  rows.push(['RECEIVABLES AGING']);
  const recAging = computeReceivablesAging(allInvoices);
  const recMonths = [...new Set(Object.values(recAging).flatMap(m => Object.keys(m)))].sort().reverse();
  rows.push(['Customer', ...recMonths, 'Total']);
  Object.entries(recAging)
    .sort((a, b) => Object.values(b[1]).reduce((s, v) => s + v, 0) - Object.values(a[1]).reduce((s, v) => s + v, 0))
    .forEach(([name, months]) => {
      const total = Object.values(months).reduce((s, v) => s + v, 0);
      rows.push([safe(name), ...recMonths.map(m => curr(months[m] || 0)), curr(total)]);
    });
  rows.push([]);

  // Payables aging
  rows.push(['PAYABLES AGING']);
  const payAging = computePayablesAging(openPayables);
  const payMonths = [...new Set(Object.values(payAging).flatMap(m => Object.keys(m)))].sort().reverse();
  rows.push(['Supplier', ...payMonths, 'Total']);
  Object.entries(payAging)
    .sort((a, b) => Object.values(b[1]).reduce((s, v) => s + v, 0) - Object.values(a[1]).reduce((s, v) => s + v, 0))
    .forEach(([supplier, months]) => {
      const total = Object.values(months).reduce((s, v) => s + v, 0);
      rows.push([safe(supplier), ...payMonths.map(m => curr(months[m] || 0)), curr(total)]);
    });

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 28 }, { wch: 30 }, { wch: 14 }, { wch: 22 }, { wch: 18 }];
  applyNumberFormat(ws, rows);
  XLSX.utils.book_append_sheet(wb, ws, 'Breakdown');
}

// ---------------------------------------------------------------------------
// Sheet 4 – Summary
// ---------------------------------------------------------------------------
function buildSummarySheet(wb, data) {
  const { monthInvoices, monthStart } = data;
  const summary = computeMonthlySummary(monthInvoices);
  const rows = [];

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  rows.push([`MONTHLY SUMMARY — ${monthNames[monthStart.getMonth()]} ${monthStart.getFullYear()}`]);
  rows.push([]);

  rows.push(['Metric', 'Amount']);
  rows.push(['Total Income (Invoice Value)', curr(summary.totalIncome)]);
  rows.push(['Invoices Count', summary.invoiceCount]);
  rows.push([]);
  rows.push(['Spare Parts Sale', curr(summary.totalSpareSale)]);
  rows.push(['Spare Parts Cost', curr(summary.totalSpareCost)]);
  rows.push(['Spare Part Margin (Sale - Cost)', curr(summary.sparePartMargin)]);
  rows.push([]);
  rows.push(['Labour Revenue', curr(summary.totalLabour)]);
  rows.push([]);
  rows.push(['Gross Profit (Margin + Labour)', curr(summary.grossProfit)]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 36 }, { wch: 20 }];
  applyNumberFormat(ws, rows);
  XLSX.utils.book_append_sheet(wb, ws, 'Summary');
}

// ---------------------------------------------------------------------------
// Utility: apply number formatting to cells containing numbers
// ---------------------------------------------------------------------------
function applyNumberFormat(ws, rows) {
  rows.forEach((row, ri) => {
    row.forEach((cell, ci) => {
      if (typeof cell === 'number') {
        const addr = XLSX.utils.encode_cell({ r: ri, c: ci });
        if (ws[addr]) {
          ws[addr].z = NUM_FMT;
        }
      }
    });
  });
}
